import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { parseCSV, validateDataDictionaryCSVFormat } from '../../utils/dataDictionaryUtils';

/**
 * CSVPreviewModal - Presentational component for previewing CSV file before upload
 * Shows file contents in a table format with ability to confirm or cancel
 */
function CSVPreviewModal({
  isOpen,
  file,
  title,
  onConfirm,
  onCancel,
  onClose,
  isLoading,
  dictionaryType = 'recipes',
}) {
  const [csvData, setCSVData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !file) {
      setCSVData(null);
      setError(null);
      return;
    }

    const loadCSV = async () => {
      try {
        setError(null);
        setCSVData(null);
        
        // First, validate the file content format based on dictionary type
        const fileContent = await file.text();
        const formatValidation = validateDataDictionaryCSVFormat(fileContent, dictionaryType);
        
        if (!formatValidation.valid) {
          setError(formatValidation.error);
          return;
        }
        
        // Then parse the CSV
        const data = await parseCSV(file);
        if (data && data.headers && data.headers.length > 0 && data.rows && data.rows.length > 0) {
          setCSVData(data);
        } else if (!data || !data.headers || data.headers.length === 0) {
          setError('CSV file is empty or has no headers');
        } else if (!data.rows || data.rows.length === 0) {
          setError('CSV file has headers but no data rows');
        }
      } catch (err) {
        console.error('CSV parsing error:', err);
        setError(`Failed to read CSV file: ${err.message}`);
      }
    };

    loadCSV();
  }, [isOpen, file, dictionaryType]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            {file && (
              <p className="text-sm text-gray-500 mt-1">
                File: <span className="font-medium">{file.name}</span>
              </p>
            )}
          </div>
          <button
            onClick={() => {
              onClose?.();
              onCancel();
            }}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 whitespace-pre-wrap font-mono text-sm leading-relaxed">{error}</p>
            </div>
          ) : csvData ? (
            <div>
              {/* Column Info */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <span className="font-semibold">{csvData.headers.length}</span> columns, <span className="font-semibold">{csvData.rows.length}</span> data rows
                </p>
              </div>

              {/* Table */}
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr className="divide-x divide-gray-200">
                        {csvData.headers.map((header, idx) => (
                          <th
                            key={idx}
                            className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gray-100"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {csvData.rows.slice(0, 50).map((row, rowIdx) => (
                        <tr
                          key={rowIdx}
                          className="divide-x divide-gray-200 hover:bg-blue-50 transition-colors"
                        >
                          {row.map((cell, cellIdx) => (
                            <td
                              key={cellIdx}
                              className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis"
                              title={cell}
                            >
                              {cell || <span className="text-gray-400 italic">empty</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-4 text-sm text-gray-600">
                {csvData.rows.length > 50 ? (
                  <p>
                    Showing first <span className="font-medium">50</span> of <span className="font-medium">{csvData.rows.length}</span> rows
                  </p>
                ) : (
                  <p>
                    Total rows: <span className="font-medium">{csvData.rows.length}</span>
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4" />
                <p className="text-gray-600">Loading CSV preview...</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading || error || !csvData}
            className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Uploading...
              </>
            ) : (
              'Upload'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

CSVPreviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  file: PropTypes.instanceOf(File),
  title: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  isLoading: PropTypes.bool,
  dictionaryType: PropTypes.oneOf(['recipes', 'products', 'events', 'subevents']),
};

CSVPreviewModal.defaultProps = {
  file: null,
  onClose: null,
  isLoading: false,
  dictionaryType: 'recipes',
};

export default CSVPreviewModal;
